import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { getRecaptchaHeaders } from '@/features/application/utils/recaptcha-token.utils';
import type { LegalContext } from '../types/legal-context.types';

export interface ApplicationApiResponse {
  // Redirect responses
  redirectToSigner?: string;
  redirectToRecord?: string;

  // Form payload responses
  structure?: any;
  currentSignerIndex?: number;
  recordStatus?: string;
  allSigners?: any[];
  allSignersComplete?: boolean;
  currentSignerCompleted?: boolean;
  userId?: number;
  primaryCrmObject?: string;
  crmId?: string | null;
  brokerTrialEnded?: boolean;
  progress?: {
    lastCompletedPageIndex: number;
    lastCompletedPageId: string | null;
    lastSeenTotalPages: number;
  };
  legalContext?: LegalContext | null;
}

export interface FetchApplicationDataParams {
  shortId: string;
  mappingShortId: string;
  recordId?: string;
  signerShortId?: string;
  signerIndex?: number;
  pdfOnly?: boolean;
}

// Purpose: Fetch application data from the backend API
export const fetchApplicationData = async (
  params: FetchApplicationDataParams
): Promise<ApplicationApiResponse> => {
  const { shortId, mappingShortId, recordId, signerShortId, signerIndex, pdfOnly } = params;

  const logContext = {
    service: 'applicationApi',
    function: 'fetchApplicationData',
    shortId,
    mappingShortId,
    recordId,
    signerShortId,
  };

  // Build the API endpoint using query params per new contract
  const endpoint = `/api/record-actions/form/${shortId}/${mappingShortId}`;

  // Add query parameters if needed
  const queryParams = new URLSearchParams();
  if (recordId) {
    // recordId now represents crmId
    queryParams.append('crmId', recordId);
  }
  if (typeof signerIndex === 'number' && !Number.isNaN(signerIndex)) {
    queryParams.append('signerIndex', String(signerIndex));
  }
  if (pdfOnly) {
    queryParams.append('pdfOnly', '1');
  }
  
  const fullEndpoint = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

  try {
    const recaptchaHeaders = await getRecaptchaHeaders('load_application');
    const response = await api.get<ApplicationApiResponse>(fullEndpoint, {
      headers: recaptchaHeaders,
    });

    return response.data;
  } catch (error) {
    logger.error('Application API', 'API request failed', {
      ...logContext,
      endpoint,
      error,
    });
    throw error;
  }
};

/**
 * Fetches mapping information by short ID for application form setup.
 * @param userShortId The short ID of the user.
 * @param mappingShortId The short ID of the mapping.
 * @returns The mapping information needed for application setup.
 */
export const fetchMappingInfoByShortId = async (
  userShortId: string,
  mappingShortId: string
): Promise<{ primaryCrmObject: string; userId: number; mappingName: string }> => {
  const logContext = {
    service: 'applicationApi',
    function: 'fetchMappingInfoByShortId',
    userShortId,
    mappingShortId,
  };

  try {
    const response = await api.get<{
      primaryCrmObject: string;
      userId: number;
      mappingName: string;
    }>(`/api/field-mapping/validate-mapping/${userShortId}/${mappingShortId}`);

    return response.data;
  } catch (error) {
    logger.error('Application API', 'Failed to fetch mapping info', {
      ...logContext,
      error,
    });
    throw error;
  }
};
